import { Alert } from "PFComponents/alert";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useBooking } from "PFCore/hooks/queries";
import { useProfile } from "PFCore/hooks/queries/profile/use_profile";
import { Booking } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./booking_placeholder_alert.module.scss";
import { usePlaceholderBooking } from "./hooks/use_placeholder_booking";

type BookingPlaceholderAlertProps = {
  booking: Booking | undefined;
};

export const BookingPlaceholderAlert = ({ booking }: BookingPlaceholderAlertProps) => {
  const { t } = useTranslation("bookingModule");

  const { isBookingPlaceholder } = usePlaceholderBooking();

  const linkedBookingId = useMemo(
    () => booking?.metadata?.placeholder_booking_id ?? booking?.metadata?.original_booking_id,
    [booking]
  );

  const { data: linkedBooking, isLoading: isLinkedBookingLoading } = useBooking(linkedBookingId, {
    enabled: !!linkedBookingId
  });

  const { data: linkedBookingProfile, isLoading: isLinkedBookingProfileLoading } = useProfile(
    linkedBooking?.profile_id,
    {
      enabled: !!linkedBooking?.profile_id
    }
  );
  const profileName = getProfileName(linkedBookingProfile);

  const reassignLabel = isBookingPlaceholder(booking)
    ? t("bookings.reassign.reassignedFrom", { profileName })
    : t("bookings.reassign.reassignedTo", { profileName });

  const waitingForApprovalLabel = t("bookings.reassign.waitingForApproval");

  const label = `${waitingForApprovalLabel} ${reassignLabel}`;

  const isLoading = isLinkedBookingLoading || isLinkedBookingProfileLoading;

  return (
    <Alert kind="warning" className={css.alert}>
      <Inline alignItems="center">
        <Icon name="hourglass-half" color="paletteOrange1" />
        {isLoading ? <LoadingDots /> : <Typography variant="labelRegular">{label}</Typography>}
      </Inline>
    </Alert>
  );
};
